import { Fragment, useContext, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/solid";
import { NavLink, useLocation } from "react-router-dom";

import AppLogo from "../../assets/images/app-logo.png";
import OnboardingAppLogo from "../../assets/images/onboarding-app-logo.png";
import { SignIn } from "../SignIn";
import {
  ChainIcon,
  ShareIcon,
  StarIcon,
  UserIcon,
} from "../../Icons";
import { UserContext } from "../../context/userContext";
import { navigation, faq, assets } from "../Sidebar/items";
import { analytics } from "../../helpers/analytics";
import { logout } from "../../helpers/magic";
import { PaywallModal } from "../PaywallModal";
import { SubscriptionModal } from "../SubscriptionModal";
import { Notification } from "../Notification";

type HeaderAuthControlProps = {
  onPremiumClick?: () => void;
};

const HeaderAuthControl = ({ onPremiumClick }: HeaderAuthControlProps) => {
  const [isAuthOpen, setAuthOpen] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const isLoggedIn = Boolean(user && user.email);
  const hasActiveSubscription = Boolean(
    user?.subscriptionEnd && user.subscriptionEnd > Date.now()
  );
  const subscriptionActionLabel = user?.isCanceled
    ? "Manage subscription"
    : "Cancel subscription";

  const handleAuthButtonClick = () => {
    analytics.track("Auth Modal Opened", {
      source: "header",
      is_logged_in: isLoggedIn,
    });
    setAuthOpen(true);
  };

  const handleLogoutClick = async () => {
    analytics.track("Logout Clicked", { source: "header_menu" });
    await logout(setUser);
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    analytics.track("Auth Modal Completed", { source: "header" });
    setAuthOpen(false);
  };

  return (
    <>
      {isLoggedIn ? (
        <Menu as="div" className="relative shrink-0">
          <Menu.Button
            type="button"
            aria-label={`Profile: ${user.email}`}
            title={user.email}
            style={{ outline: "none" }}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-light-blue text-main-blue"
          >
            <UserIcon className="h-6 w-6" aria-hidden="true" />
          </Menu.Button>
          <HeaderMenuItems className="right-0 w-56">
            <div className="border-b border-main-grey-300 px-3 py-2">
              <p className="truncate text-xs font-medium text-main-blue">
                {user.email}
              </p>
            </div>
            {onPremiumClick && hasActiveSubscription && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={`flex w-full items-center px-3 py-2 text-left text-xs font-medium ${
                      active ? "bg-main-grey-200" : ""
                    }`}
                    onClick={onPremiumClick}
                  >
                    <StarIcon
                      className="mr-2 h-[18px] w-[18px] shrink-0"
                      aria-hidden="true"
                    />
                    {subscriptionActionLabel}
                  </button>
                )}
              </Menu.Item>
            )}
            {onPremiumClick && !hasActiveSubscription && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={`flex w-full items-center px-3 py-2 text-left text-xs font-medium ${
                      active ? "bg-main-grey-200" : ""
                    }`}
                    onClick={onPremiumClick}
                  >
                    <StarIcon
                      className="mr-2 h-[18px] w-[18px] shrink-0"
                      aria-hidden="true"
                    />
                    Premium
                  </button>
                )}
              </Menu.Item>
            )}
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  className={`flex w-full items-center px-3 py-2 text-left text-xs font-medium ${
                    active ? "bg-main-grey-200" : ""
                  }`}
                  onClick={handleLogoutClick}
                >
                  Log out
                </button>
              )}
            </Menu.Item>
          </HeaderMenuItems>
        </Menu>
      ) : (
        <div className="flex shrink-0 items-center gap-2 sm:gap-1">
          <button
            type="button"
            style={{ outline: "none" }}
            className="h-9 rounded-md px-3 text-xs font-bold text-main-blue hover:bg-light-blue sm:hidden"
            onClick={handleAuthButtonClick}
          >
            Log in
          </button>
          <button
            type="button"
            style={{ outline: "none" }}
            className="h-9 rounded-md bg-main-blue px-3 text-xs font-bold text-white hover:bg-[#5633df] sm:px-2"
            onClick={handleAuthButtonClick}
          >
            Sign up
          </button>
        </div>
      )}

      <SignIn
        isOpen={isAuthOpen}
        onClose={handleAuthClose}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
};

type HeaderMenuItemsProps = {
  children: React.ReactNode;
  className?: string;
};

const HeaderMenuItems = ({ children, className = "" }: HeaderMenuItemsProps) => {
  return (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-150"
      enterFrom="opacity-0 translate-y-1"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-1"
    >
      <Menu.Items
        className={`absolute top-full z-20 mt-2 overflow-hidden rounded-lg border border-main-grey-300 bg-white py-1 shadow-lg ${className}`}
      >
        {children}
      </Menu.Items>
    </Transition>
  );
};

type TopNavLinkProps = {
  isExternal: boolean;
  name: string;
  href: string;
  Icon?: React.ElementType;
  compact?: boolean;
};

const trackNavClick = (name: string) => {
  analytics.track("Navigation Clicked", { label: name, location: "header" });
  if (name !== "How to install?") {
    analytics.track("Category Selected", { category: name });
  }
  if (name === "Academy") {
    analytics.track("Academy Homeview", { category: name });
  }
};

const TopNavLink = ({
  isExternal,
  name,
  href,
  Icon,
  compact = false,
}: TopNavLinkProps) => {
  const baseClass = compact
    ? "flex w-full items-center px-3 py-2 text-left text-xs font-medium"
    : "flex h-9 shrink-0 items-center rounded-full px-3 text-xs font-bold transition-colors";
  const inactiveClass = compact
    ? "text-black hover:bg-main-grey-200"
    : "text-black hover:bg-main-grey-200";
  const activeClass = compact
    ? "bg-main-grey-200 text-main-blue"
    : "bg-black text-white hover:bg-black";

  if (isExternal) {
    return (
      <a
        href={href}
        className={`${baseClass} ${inactiveClass}`}
        style={{ outline: "none" }}
        onClick={() =>
          analytics.track("External Navigation Clicked", {
            label: name,
            href,
            location: "header",
          })
        }
      >
        {Icon && (
          <Icon
            className="mr-2 h-[18px] w-[18px] shrink-0"
            aria-hidden="true"
          />
        )}
        {name}
      </a>
    );
  }

  return (
    <NavLink
      to={href}
      end={href === "/"}
      className={({ isActive }) =>
        `${baseClass} ${isActive ? activeClass : inactiveClass}`
      }
      onClick={() => trackNavClick(name)}
      style={{ outline: "none" }}
    >
      {Icon && (
        <Icon
          className="mr-2 h-[18px] w-[18px] shrink-0"
          aria-hidden="true"
        />
      )}
      {name}
    </NavLink>
  );
};

const HeaderMoreMenu = ({
  onShareClick,
}: {
  onShareClick: () => void;
}) => {
  return (
    <Menu as="div" className="relative shrink-0">
      <Menu.Button
        type="button"
        className="flex h-9 items-center rounded-full bg-main-grey-200 px-3 text-xs font-bold text-black hover:bg-main-grey-500"
        style={{ outline: "none" }}
      >
        More
      </Menu.Button>
      <HeaderMenuItems className="right-0 w-56">
        {[...faq, ...assets].map((item) => (
          <Menu.Item key={item.name}>
            {({ active }) => (
              <TopNavLink
                name={item.name}
                href={item.href}
                Icon={item.icon}
                isExternal={item.isExternal}
                compact
              />
            )}
          </Menu.Item>
        ))}
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              className={`flex w-full items-center px-3 py-2 text-left text-xs font-medium ${
                active ? "bg-main-grey-200" : ""
              }`}
              style={{ outline: "none" }}
              onClick={onShareClick}
            >
              <ShareIcon
                className="mr-2 h-[18px] w-[18px] shrink-0"
                aria-hidden="true"
              />
              Share our app
            </button>
          )}
        </Menu.Item>
      </HeaderMenuItems>
    </Menu>
  );
};

const Header = () => {
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [isSubscriptionStatusOpen, setSubscriptionStatusOpen] = useState(false);
  const [isNotificationVisible, setNotificationVisible] = useState(false);
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const {
    hasPremium = false,
    subscriptionEnd = null,
    isCanceled = null,
    plan = null,
  } = user || {};
  const isSearchLanding = new URLSearchParams(location.search).has("search");

  const handlePremiumClick = () => {
    analytics.track("Premium CTA Clicked", {
      source: "header",
      is_logged_in: Boolean(user && user.email),
      has_premium: hasPremium,
      subscription_active: Boolean(subscriptionEnd && subscriptionEnd > Date.now()),
      trial: plan?.isTrial || false,
    });

    if (user && user.email) {
      if (subscriptionEnd && subscriptionEnd > Date.now()) {
        analytics.track("Subscription Status Opened", {
          source: "header",
          trial: plan?.isTrial || false,
          is_canceled: isCanceled,
        });
        setSubscriptionStatusOpen(true);
      } else {
        analytics.track("Paywall", {
          premium: hasPremium,
          trial: false,
          source: "header",
        });
        setPaywallOpen(true);
      }
    } else {
      analytics.track("Auth Modal Opened", {
        source: "premium_cta",
        is_logged_in: false,
      });
      setAuthOpen(true);
    }
  };

  const handleRenewalClick = () => {
    analytics.track("Paywall Opened From Subscription Renewal", {
      source: "subscription_status",
    });
    setSubscriptionStatusOpen(false);
    setPaywallOpen(true);
  };

  const handleCancelClick = () => {
    analytics.track("Subscription Cancellation Reflected", {
      source: "subscription_status",
    });
    if (user) {
      setUser({ ...user, isCanceled: true });
    }
  };

  const shareButtonClick = () => {
    analytics.track("Share Link Copy Started", {
      source: "header_more_menu",
    });
    navigator.clipboard
      .writeText("https://toolsforprocreate.onelink.me/zChQ/web")
      .then(() => {
        analytics.track("Share Link Copied", {
          source: "header_more_menu",
        });
        setNotificationVisible(true);
      })
      .catch((error) => {
        analytics.track("Share Link Copy Failed", {
          source: "header_more_menu",
          error_message: error instanceof Error ? error.message : String(error),
        });
      });
  };

  useEffect(() => {
    if (isNotificationVisible) {
      const timer = setTimeout(() => setNotificationVisible(false), 1500);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  return (
    <div className="sticky top-0 z-10 border-b border-main-grey-300 bg-white">
      <div className="px-8 py-4 sm:px-4 sm:py-3">
        <div className="flex items-center justify-between gap-4">
          <a className="flex min-w-0 shrink-0 items-center" href="/">
            <img
              src={AppLogo}
              alt="Tools for Procreate logo"
              className="h-[39px] w-[39px] shrink-0 rounded-[10px]"
            />
            <div className="ml-3 min-w-0 text-left">
              <p className="truncate text-lg font-bold leading-lg">Tools</p>
              <p className="truncate text-xxs font-medium leading-xxs text-main-grey-800">
                Tools for Procreate
              </p>
            </div>
          </a>
          <nav className="flex min-w-0 flex-1 items-center justify-center gap-1 lg:hidden">
            {navigation.map((item) => (
              <TopNavLink
                key={item.name}
                name={item.name}
                href={item.href}
                Icon={item.icon}
                isExternal={item.isExternal}
              />
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-2">
            {!isSearchLanding && (
              <button
                className={`flex h-9 shrink-0 items-center rounded-full px-3 text-xs font-bold lg:hidden ${
                  hasPremium && user && user.email
                    ? "bg-light-blue text-main-blue"
                    : "bg-main-blue text-white hover:bg-[#5633df]"
                }`}
                type="button"
                onClick={handlePremiumClick}
                style={{ outline: "none" }}
              >
                <StarIcon
                  className="mr-2 h-[18px] w-[18px] shrink-0"
                  aria-hidden="true"
                />
                {hasPremium && user && user.email ? "Premium" : "Upgrade"}
                {hasPremium && user && user.email && (
                  <InformationCircleIcon className="ml-1 h-[14px] w-[14px] text-main-blue" />
                )}
              </button>
            )}
            <HeaderMoreMenu onShareClick={shareButtonClick} />
            <HeaderAuthControl onPremiumClick={handlePremiumClick} />
          </div>
        </div>
        <div className="mt-3 hidden items-center gap-2 overflow-x-auto pb-1 lg:flex">
          {!isSearchLanding && (
            <button
              className={`flex h-9 shrink-0 items-center rounded-full px-3 text-xs font-bold ${
                hasPremium && user && user.email
                  ? "bg-light-blue text-main-blue"
                  : "bg-main-blue text-white hover:bg-[#5633df]"
              }`}
              type="button"
              onClick={handlePremiumClick}
              style={{ outline: "none" }}
            >
              <StarIcon
                className="mr-2 h-[18px] w-[18px] shrink-0"
                aria-hidden="true"
              />
              {hasPremium && user && user.email ? "Premium" : "Upgrade"}
              {hasPremium && user && user.email && (
                <InformationCircleIcon className="ml-1 h-[14px] w-[14px] text-main-blue" />
              )}
            </button>
          )}
          {navigation.map((item) => (
            <TopNavLink
              key={item.name}
              name={item.name}
              href={item.href}
              Icon={item.icon}
              isExternal={item.isExternal}
            />
          ))}
        </div>
      </div>
      <SignIn
        isOpen={isAuthOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={() => setAuthOpen(false)}
      />
      <PaywallModal
        isOpen={isPaywallOpen}
        onClose={() => setPaywallOpen(false)}
        userId={(user && user.id) || null}
        email={(user && user.email) || null}
        hasPremium={hasPremium}
        isTrial={(plan && plan.isTrial) || false}
      />
      {Boolean(user && user.email && subscriptionEnd && plan) && (
        <SubscriptionModal
          isOpen={isSubscriptionStatusOpen}
          onClose={() => setSubscriptionStatusOpen(false)}
          endDate={subscriptionEnd}
          onRenewal={handleRenewalClick}
          email={user.email}
          isCanceled={isCanceled}
          onCancel={handleCancelClick}
          plan={plan}
        />
      )}
      {isNotificationVisible && (
        <Notification
          icon={<ChainIcon className="w-[20px]" />}
          text="Share link copied"
          color="#000000"
        />
      )}
    </div>
  );
};

const OnboardingHeader = () => {
  return (
    <div className="z-50 sticky top-0 bg-white z-10">
      <div className="py-5 px-6 flex justify-between items-center">
        <a className="flex" href="/">
          <img
            src={OnboardingAppLogo}
            alt="app logo"
            className="shrink-0 w-[39px] h-[39px] rounded-[10px]"
          />
          <div className="text-left ml-4">
            <p className="text-lg leading-lg font-bold">Tools</p>
            <p className="text-xxs leading-xxs font-medium text-main-grey-800">
              The best tools for drawing
            </p>
          </div>
        </a>

        <HeaderAuthControl />
      </div>
    </div>
  );
};

export { Header, OnboardingHeader };
